// One-line horizontal bar used everywhere we render a labelled count
// (top BUs, locations, attrition rows, pillars). Optional click handler.

import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';

import { formatNumberFull } from '../../../utils/FormatUtils';

export interface HBarRowProps {
  label: React.ReactNode;
  sublabel?: React.ReactNode;
  count: number;
  /** Largest count in the rendering set; used to compute bar width. */
  maxCount: number;
  /** Bar fill colour. */
  color: string;
  onClick?: () => void;
  /** Trailing element (e.g. chevron, percentage chip). */
  trailing?: React.ReactNode;
  /** Optional left-side accent strip (e.g. pillar colour). */
  accentStripeColor?: string;
}

const HBarRow: React.FC<HBarRowProps> = ({
  label,
  sublabel,
  count,
  maxCount,
  color,
  onClick,
  trailing,
  accentStripeColor,
}) => {
  const pct = maxCount > 0 ? Math.min((count / maxCount) * 100, 100) : 0;
  const inner = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: accentStripeColor
          ? '4px minmax(0, 1fr) 1fr 64px auto'
          : 'minmax(0, 1fr) 1fr 64px auto',
        alignItems: 'center',
        columnGap: 1.5,
        width: '100%',
        py: 1,
        px: 1.25,
        borderRadius: 1.5,
        textAlign: 'left',
      }}
    >
      {accentStripeColor && (
        <Box
          sx={{
            width: 4,
            height: 28,
            borderRadius: 1,
            backgroundColor: accentStripeColor,
          }}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        {sublabel && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          height: 6,
          width: '100%',
          backgroundColor: '#F1F5F9',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: color,
            borderRadius: 999,
            transition: 'width 220ms ease',
          }}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          color: 'text.primary',
        }}
      >
        {formatNumberFull(count)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        {trailing}
      </Box>
    </Box>
  );

  if (!onClick) return inner;
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        width: '100%',
        borderRadius: 1.5,
        '&:hover': { backgroundColor: '#F8FAFC' },
      }}
    >
      {inner}
    </ButtonBase>
  );
};

export default HBarRow;
